import i18next from 'i18next';
import moment from 'moment';
import 'moment/min/locales';

import { resources } from 'locales';
import config from "config";

import dom from "actions/helper/dom";
import dates from "actions/helper/dates";
import array from "actions/helper/array";
import string from "actions/helper/string";
import number from "actions/helper/number";
import xlsx from "actions/helper/xlsx";
import object from "actions/helper/object";
import inputValidators from "actions/helper/inputValidators";

import { ReactComponent as FirstVisitIcon } from 'icons/pages/app/firstVisit.svg';
import { ReactComponent as BanIcon } from 'icons/pages/app/ban.svg';

const { langs, defaultLang, clientStatuses, replaceReasons } = config;
const lng = localStorage.language || defaultLang;

i18next.init({
    resources,
    fallbackLng: langs,
    lng,
    debug: false
});

moment.locale(lng);

const obj = {
    date: dates,
    dom,
    string,
    array,
    number,
    xlsx,
    object,
    inputValidators,
    i18next,
    moment,
    currentLanguage: lng,

    setupOnStart() {
        const lastDateStr = this.storage("apptSelectedClients")?.date;
        const lastDate = this.date.clearTime(lastDateStr);
        const currDate = this.date.clearTime();

        if (lastDate.getTime() < currDate.getTime()) {
            this.storage("apptSelectedClients", null);
            this.storage("apptLastActiveClient", null);
        }
    },

    get transactionTypes() {
        return config.transactionType;
    },

    tr(prefix, key) {
        if (prefix && prefix[0] === "^")
            return prefix.substring(1);

        if (arguments.length === 2)
            return i18next.t(prefix + "." + key);

        return i18next.t(prefix);
    },

    storage(key, value) {
        const l = arguments.length;

        if (l < 1)
            return localStorage;
        if (l === 1) {
            let data = localStorage.getItem(key);
            if (data && data[0] === "{")
                data = JSON.parse(data).value;

            return data;
        }
        localStorage.setItem(key, JSON.stringify({ value }));
        return value;
    },

    disconnect(it) {
        if (it?.remove?.apply)
            it.remove();
        else if (it?.forEach?.apply) {
            it.forEach(ev => this.disconnect(ev));
        }
    },

    on(target, eventName, fn, useCapture) {
        const capt = !!useCapture;

        target.addEventListener(eventName, fn, capt);

        return {
            "remove": () => {
                target.removeEventListener(eventName, fn, capt);
            }
        };
    },

    click(target, fn, useCapture) {
        return this.on(target, "click", fn, useCapture);
    },

    stopEvent(e) {
        if (e?.preventDefault?.apply)
            e.preventDefault();

        if (e?.stopPropagation?.apply)
            e.stopPropagation();
    },

    login(token) {
        localStorage.setItem('token', token);
        globalThis.appView.setLoginState(true);
    },

    logout() {
        localStorage.setItem('token', '');
        localStorage.setItem('cacheData', '');
        localStorage.setItem('cacheDates', '');
        globalThis.appView.setLoginState(false);
    },

    showRequestError(resp, errText) {
        this.hideLoading();

        const label = this.tr(`server.${resp?.code || "error"}`);
        const text = errText || resp?.message || this.tr("server.requestError");
        this.showNotifyPopup({
            type: "error",
            label,
            text,
        });
    },

    showRequestSuccess(resp, label, text) {
        if (resp?.code === "success")
            this.showNotify(label, text);
    },

    showNotify(label, text) {
        this.showNotifyPopup({
            type: "success",
            label: this.tr(label),
            text: this.tr(text),
        });
    },

    showNotifyPopup(props) {
        globalThis.appNotifyPopup.show(props);
    },

    showLoading() {
        globalThis.appLoading.classList.add("active");
    },

    hideLoading() {
        if (globalThis.appLoading)
            globalThis.appLoading.classList.remove("active");
    },

    execCall(phone) {
        const a = document.createElement("a");
        a.href = "tel:" + phone;

        a.click();
    },

    showImageBox(id, src, list = []) {
        const node = globalThis.appImageBox;

        node.innerHTML = "";

        const img = document.createElement("img");
        const close = document.createElement("div");

        this.dom.addClass(close, ["closeImg", "hand"]);
        this.dom.addClass(img, "transit06");
        this.dom.addClass(node, "active");

        node.appendChild(close);

        let currentIndex = list.length > 0 ? list.indexOf(src) : -1;
        let isImageLoading = false; // Флаг, чтобы предотвратить наложение изображений

        if (list.length > 0) {
            const leftArrow = document.createElement("div");
            const rightArrow = document.createElement("div");

            this.dom.addClass(leftArrow, ["leftArrow", "hand"]);
            this.dom.addClass(rightArrow, ["rightArrow", "hand"]);

            leftArrow.innerHTML = "&larr;";
            rightArrow.innerHTML = "&rarr;";

            node.appendChild(leftArrow);
            node.appendChild(rightArrow);

            leftArrow.onclick = (event) => {
                event.stopPropagation();
                if (isImageLoading) return;
                currentIndex = (currentIndex - 1 + list.length) % list.length;
                img.src = list[currentIndex];
                isImageLoading = true;
            };

            rightArrow.onclick = (event) => {
                event.stopPropagation();
                if (isImageLoading) return;
                currentIndex = (currentIndex + 1) % list.length;
                img.src = list[currentIndex];
                isImageLoading = true;
            };
        }

        img.onload = () => {
            isImageLoading = false; // Сбрасываем флаг после загрузки
            this.dom.addClass(node, "loaded");
            node.appendChild(img);

            const tm = setTimeout(() => {
                clearTimeout(tm);
                this.dom.addClass(img, "animate");
            }, 100);
        };

        if (!node.hideImageBoxCnt)
            node.hideImageBoxCnt = this.click(node, this.hideImageBox.bind(this));

        img.src = src ? src : globalThis.appPageView._request.images.getAvatar(id);

        if (list.length > 0) {
            const handleKeyDown = (event) => {
                if (isImageLoading) return; // Блокируем обработку до завершения загрузки текущего изображения

                if (event.key === "ArrowRight") {
                    currentIndex = (currentIndex + 1) % list.length;
                    img.src = list[currentIndex];
                    isImageLoading = true;
                } else if (event.key === "ArrowLeft") {
                    currentIndex = (currentIndex - 1 + list.length) % list.length;
                    img.src = list[currentIndex];
                    isImageLoading = true;
                }
            };

            document.addEventListener("keydown", handleKeyDown);

            const originalHideImageBox = this.hideImageBox.bind(this);
            this.hideImageBox = () => {
                document.removeEventListener("keydown", handleKeyDown);
                this.hideImageBox = originalHideImageBox;
                originalHideImageBox();
            };
        }
    },

    hideImageBox() {
        const node = globalThis.appImageBox;

        this.dom.removeClass(node, "active loaded");

        node.innerHTML = "";
    },

    getClientStatus(status, withIcon = false, statusTreatment) {
        const id = status || "UNKNOWN";
        const { name } = clientStatuses.find(it => it.id === id) || {};

        if (withIcon) {
            return (
                <div className="flex centerV gap8">
                    {this.getClientStatusIcon(status)}
                    {this.tr(name)}
                    <p></p>
                    {statusTreatment && this.tr(`clientStatuses.${statusTreatment}`)}
                </div>
            );
        }

        return this.tr(name);
    },

    getClientStatusIcon(status) {
        const id = status || "UNKNOWN";

        if (id === "NOVICE")/*if (id === "CREATED" || id === "PRIMARY")*/
            return <FirstVisitIcon />;
        if (id === "BLACK_LIST")
            return <BanIcon />;

        return <></>;
    },

    openClient(id, props = {}) {
        this.openPage("client", { id, ...props });
    },

    openEmployee(id, props = {}) {
        this.openPage("employee", { id, ...props });
    },

    formUrlHash(props) {
        const str = Object.entries(props).map(([key, value]) => {
            return `${key}=${value}`;
        }).join("&");

        return str ? `#${str}` : "";
    },

    openPage(page, props = {}) {
        let propsStr = this.formUrlHash(props);
        window.open(page + propsStr, '_blank').focus();
    },

    showTooltip(node) {
        if (!node?.vTooltipText)
            return;

        if (this.isTooltipActive)
            this.hideTooltip();

        globalThis.appTooltip.innerHTML = node.vTooltipText;

        const maxWidth = node.dataset.tooltipMaxWidth;
        this.dom.style(globalThis.appTooltip, "maxWidth", maxWidth ? `${maxWidth}px` : "");

        const { x, y, wVis, hVis } = this.dom.coords(node);

        const { offsetWidth, offsetHeight } = document.body;
        const c = this.dom.coords(globalThis.appTooltip);

        let l = x;
        let t = y - c.h - 8;

        if (c.w + l + 15 > offsetWidth)
            l = (x + wVis) - c.w;
        if (t < 15)
            t = y + hVis + 8;
        if (t + c.h + 8 > offsetHeight)
            t = offsetHeight - 8 - c.h;

        this.dom.style(globalThis.appTooltip, {
            left: `${l}px`,
            top: `${t}px`,
            maxWidth: maxWidth ? `${maxWidth}px` : ""
        });

        this.dom.addClass(globalThis.appTooltip, "active");

        this.isTooltipActive = true;
    },

    hideTooltip() {
        this.dom.removeClass(globalThis.appTooltip, "active");

        this.dom.style(globalThis.appTooltip, {
            left: "",
            top: "",
            maxWidth: "",
        });

        globalThis.appTooltip.innerHTML = "";

        this.isTooltipActive = false;
    },

    getReason(id) {
        const item = (globalThis.appPageView.state?.reasons || []).find(it => it.id === id);

        return item?.title || this.tr("main.notIndicated");
    },

    getReplaceReason(id) {
        const item = replaceReasons.find(it => it.id === id);

        return this.tr(item?.name || "main.notIndicated");
    },

    getUrlOptions() {
        const options = {};

        window.location.hash.replace("#", "").split("&").forEach(s => {
            const [key, val] = s.split("=");
            options[key] = val || 1;
        });

        return options;
    },

    getProfileId() {
        return globalThis.appPageView.state?.profile?.id || null;
    },

    getClinicNameById(id) {
        const item = this.getClinics().find(it => it.id === id);
        return item?.name || this.tr("main.notIndicated");
    },

    getClinicIdByName(name) {
        const item = this.getClinics().find(it => it.name === name);
        return item?.name || this.tr("main.notIndicated");
    },

    getCurrClinic() {
        return this.getClinics().find(it => it.id === globalThis.appPageView.state?.clinicId) || {};
    },

    getClinics() {
        return globalThis.appPageView.state?.clinics || [];
    },

    getCities() {
        return globalThis.appPageView.state?.cities || [];
    },

    getCabinets(domainId) {
        if (domainId)
            return globalThis.appPageView.state?.cabinets?.filter(it => it.domainId === domainId && it.status === "ACTIVE") || [];

        return globalThis.appPageView.state?.cabinets?.filter(it => it.status === "ACTIVE") || [];
    },

    getCabinetInfo(cabinetId) {
        const cabinet = globalThis.appPageView.state?.cabinets.find(it => it.id === cabinetId);
        return cabinet;
    },

    getDoctorById(id) {
        return globalThis.appPageView.getDoctorById(id);
    },

    getDoctorNameById(id, format) {
        return this.string.formatFullName(this.getDoctorById(id), format);
    },

    openRightPopup() {
        globalThis.appPageView.openRightPopup(...arguments);
    },

    openDialog() {
        globalThis.appPageView.openDialog(...arguments);
    },

    confirm(props, cb, cancelCb) {
        globalThis.appPageView.openDialog("confirmAbstract", props, cb, cancelCb);
    },

    getTeethNames(arr) {
        const oc = [16, 18, 17, 15, 14, 13, 12, 11, 21, 22, 23, 24, 26, 25, 27, 28, 48,
            47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

        if (arr && arr.length > 0 && this.haveSameElements(oc, arr))
            arr = [100];

        return (arr?.length > 0 ? arr : [100]).map(it => this.getTeethName(it?.teeth || it));
    },

    getTeethName(nn) {
        const n = parseInt(nn, 10);

        if (!n || n < 100)
            return nn;

        return this.tr(n === 100 ? "client.oralCavity" : n === 101 ? "client.lowerJaw" : "client.upperJaw");
    },

    haveSameElements(a, b) {
        return a.length === b.length && a.every(element => b.includes(element));
    },

    setPageTab(tab, props = {}) {
        this.setPageProps({ tab, ...props });
    },

    setPageProps(props = {}) {
        window.location.hash = this.formUrlHash(props);
    },

    getPageTab() {
        return this.getUrlOptions()?.tab;
    },

    showPrintBox(url) {
        const node = globalThis.appPrint;

        if (!node.hideCnt) {
            node.vInner = this.dom.query(node, ".inner");

            node.hideCnt = this.click(node, this.hidePrintBox.bind(this));
            this.click(node.vInner, this.stopEvent.bind(this));
        }

        this.dom.addClass(node, "active");

        const objNode = document.createElement("object");
        node.vInner.appendChild(objNode);

        objNode.setAttribute("type", "application/pdf");
        objNode.setAttribute("data", url);
    },

    hidePrintBox() {
        const node = globalThis.appPrint;

        this.dom.removeClass(node, "active");

        node.vInner.innerHTML = "";
    },

    formatNumberText(one, two, five, n) {
        const word = this.string.getNumberText(n, one, two, five);

        return `${n}${this.string.spaceChar}${this.tr(word).toLowerCase()}`;
    },

    checkProfilePermissions(keyOrPath) {
        const id = globalThis.appPageView.state?.profile?.roleId || 100;
        const { allowedRoles } = config;

        let startObj = allowedRoles || {};
        (keyOrPath || "").split(".").forEach(key => {
            startObj = startObj[key] || {};
        });
        const perms = startObj?.join ? startObj : [];

        return perms.includes(id);
    },

    isDoctor() {
        const id = globalThis.appPageView.state?.profile?.roleId || 100;
        return (id === 4 || id === 8);
    },

    isDirector() {
        const id = globalThis.appPageView.state?.profile?.roleId || 100;
        return (id === 1);
    },

    isDepDirector() {
        const id = globalThis.appPageView.state?.profile?.roleId || 100;
        return (id === 2);
    },

    isAssistant() {
        const id = globalThis.appPageView.state?.profile?.roleId || 100;
        return (id === 5 || id === 6);
    },

    getRoleLabel(roleId) {

        const { rolesList } = config;

        let it = rolesList.find((robj) => {
            return (robj.id === roleId);
        });

        if (it !== undefined) {
            return this.tr(it.name);
        }

        return "";
    },

    getEmploeeStatusLabel(statusId) {
        const { employeeStatuses } = config;

        let it = employeeStatuses.find((robj) => {
            return (robj.id === statusId);
        });

        if (it !== undefined)
            return this.tr(it.name);


        return "";
    },

    getEmployeeExpirienceList() {

        let eyear = new Date().getFullYear();
        let syear = eyear - 60;
        let arr = [];

        for (let i = syear; i <= eyear; i++) {
            arr.push({ id: i, name: String(i) });
        }

        return arr;
    },

    getEmployeeExpirienceLabel(val) {
        return this.trInsValues("employee.experienceFromVal", val);
    },

    getSortField(sField) {

        const isDesc = sField[0] === "-";
        const direction = isDesc ? "DESC" : "ASC";
        const field = isDesc ? sField.substring(1) : sField;

        return { field, direction };

    },

    isRoleHasSpec(rId) {
        const arr = [1, 2, 3, 4, 8];

        if (arr.includes(rId))
            return true;

        return false;
    },

    trInsValues(prefix, ...rest) {
        if (prefix !== "") {
            let res = this.tr(prefix);

            if (arguments.length === 1) {
                return res;
            }

            for (let i = 1; i < arguments.length; i++) {
                res = res.replace(`$${i}`, String(arguments[i]));
            }

            return res;
        }

        return prefix;
    },

    getDoctors(search, type) {
        const { doctors } = globalThis.appPageView?.state;
        return doctors.filter(it => {
            if (search) {
                if (!(it.lname + " " + it.fname).toLowerCase().includes(search.toLowerCase()) && !it?.specs?.find(spec => spec?.specEntity?.title?.toLowerCase().includes(search)))
                    return false;
            }

            if (type === "assistent")
                return (it.roleId === 5 || it.roleId === 6);

            if (type === "coordinator")
                return (it.roleId === 12 || it.roleId === 7);

            if (!it.isActive || !it.isDoctor || it.fireDate || !it.specs.length || it.status === "FIRED")
                return false;

            return true;
        });
    },

    getFutureVisit(visitsOfClient, clientId) {
        let doctor = null;
        let date = null;

        visitsOfClient.forEach(it => {
            const { doctorId, beginDate, status } = it;
            if (new Date(beginDate) > new Date() && status === "WI_CREATED") {
                doctor = doctorId;
                date = beginDate;
            }
        });
        return { doctor, date };
    },

    getBalanceClient(invoiceList) {
        const statusValid = ["CREATED", "PAID", "PARTIALLY_PAID"];
        const totalSum = invoiceList.reduce((acc, obje) => ((obje['totalSum'] && statusValid.includes(obje.status) && obje.status !== "CANCELLED") ? acc + obje['totalSum'] : acc), 0);
        const totalPaidSum = invoiceList.reduce((acc, obje) => ((obje['paidSum'] && statusValid.includes(obje.status) && obje.status !== "CANCELLED") ? acc + obje['paidSum'] : acc), 0);
        return { totalSum, totalPaidSum };
    }
};

export default obj;
